import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { clubAPI, type ClubResponse } from '@/api/club'

const CLUB_CACHE_KEY = 'clubStore'

interface StoredClubState {
  data?: Record<string, ClubResponse>
  list?: string[]
}

const readStoredClubState = (): StoredClubState => {
  const stored = localStorage.getItem(CLUB_CACHE_KEY)
  if (!stored || stored === 'undefined' || stored === 'null') {
    return { data: {}, list: [] }
  }

  try {
    const parsed = JSON.parse(stored) as StoredClubState
    return {
      data: parsed.data || {},
      list: Array.isArray(parsed.list) ? parsed.list : [],
    }
  } catch {
    localStorage.removeItem(CLUB_CACHE_KEY)
    return { data: {}, list: [] }
  }
}

const sortClubIds = (clubIds: string[], data: Record<string, ClubResponse>) => {
  return [...clubIds].sort((a, b) =>
    (data[a]?.name || '').localeCompare(data[b]?.name || '')
  )
}

export const useClubStore = defineStore('club', () => {
  const storedState = readStoredClubState()
  const data = ref<Record<string, ClubResponse>>(storedState.data || {})
  const list = ref<string[]>(sortClubIds(storedState.list || [], data.value))
  const loading = ref(false)
  const loadedForUser = ref('')
  const error = ref<string | null>(null)

  const clubs = computed(() =>
    list.value
      .map((clubId) => data.value[clubId])
      .filter((club): club is ClubResponse => !!club)
  )

  const persist = () => {
    localStorage.setItem(
      CLUB_CACHE_KEY,
      JSON.stringify({
        data: data.value,
        list: list.value,
      })
    )
  }

  const setClubs = (nextClubs: ClubResponse[]) => {
    const nextData: Record<string, ClubResponse> = { ...data.value }
    const nextList: string[] = []

    nextClubs.forEach((club) => {
      if (!club.id) return
      nextData[club.id] = club
      nextList.push(club.id)
    })

    data.value = nextData
    list.value = sortClubIds(nextList, nextData)
    persist()
  }

  const upsertClub = (club: ClubResponse) => {
    if (!club.id) return

    const nextData: Record<string, ClubResponse> = {
      ...data.value,
      [club.id]: club,
    }
    const nextList = list.value.includes(club.id)
      ? list.value
      : [...list.value, club.id]

    data.value = nextData
    list.value = sortClubIds(nextList, nextData)
    persist()
  }

  const loadAllClubsForUser = async (userId: string) => {
    if (!userId) return

    try {
      loading.value = true
      error.value = null
      const response = await clubAPI.listClubsForUser(userId)
      setClubs(response.clubs || [])
      loadedForUser.value = userId
    } catch (err: any) {
      error.value = err.message || 'Unable to load your club rooms.'
    } finally {
      loading.value = false
    }
  }

  const clearClubState = () => {
    loadedForUser.value = ''
    data.value = {}
    list.value = []
    error.value = null
    localStorage.removeItem(CLUB_CACHE_KEY)
  }

  return {
    data,
    list,
    clubs,
    loading,
    loadedForUser,
    error,
    setClubs,
    upsertClub,
    loadAllClubsForUser,
    clearClubState,
  }
})
